/**
 * Reservation warning helpers — mirrors iOS DayView + InteractiveTimePickerView.
 */

export type ReservationWarningLevel = 'required' | 'recommended' | 'noWarnings' | string;

export interface ReservationWarningItem {
  type?: string | null;
  reservationRequirement?: string | null;
  confirmationNumber?: string | null;
}

function normalizeRequirement(raw: string | null | undefined): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase();
}

function hasConfirmation(item: ReservationWarningItem): boolean {
  return Boolean(item.confirmationNumber?.trim());
}

/**
 * Whether a day-view item should contribute to the reservation warning banner.
 * iOS warns for meals lacking a confirmation when the requirement meets the user level.
 * Activities that carry a reservationRequirement are included for web parity with plan data.
 */
export function shouldShowReservationWarning(
  item: ReservationWarningItem,
  level: ReservationWarningLevel
): boolean {
  const type = String(item.type ?? '')
    .trim()
    .toLowerCase();
  if (type !== 'meal' && type !== 'activity') return false;
  if (hasConfirmation(item)) return false;

  const req = normalizeRequirement(item.reservationRequirement);
  if (!req || req === 'not_needed' || req === 'none') return false;

  const warningLevel = String(level ?? '')
    .trim()
    .toLowerCase();
  if (!warningLevel || warningLevel === 'nowarnings' || warningLevel === 'none') {
    return false;
  }
  if (warningLevel === 'required') {
    return req === 'required';
  }
  if (warningLevel === 'recommended') {
    return req === 'required' || req === 'recommended';
  }
  return false;
}

/** Count items that should show a reservation warning at the given level. */
export function countReservationWarnings(
  items: ReservationWarningItem[],
  level: ReservationWarningLevel
): number {
  return items.filter((item) => shouldShowReservationWarning(item, level)).length;
}

/**
 * True when scheduling a meal that needs a confirmation number prompt
 * (optional — user may skip). Mirrors iOS InteractiveTimePickerView.
 */
export function needsMealConfirmationPrompt(
  itemType: string | null | undefined,
  reservationRequirement: string | null | undefined
): boolean {
  const type = String(itemType ?? '')
    .trim()
    .toLowerCase();
  if (!(type === 'meal' || type === 'food' || type === 'restaurant')) {
    return false;
  }
  const req = normalizeRequirement(reservationRequirement);
  return req === 'required' || req === 'recommended';
}
